/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – PredictionPanel.tsx FINAL
// Adăugat: Factori explicativi AI + recomandări acțiuni
// ============================================================

import React, { useState, useMemo } from 'react';
import { Tag, Table, Tooltip } from 'antd';
import {
  ExperimentOutlined, FireOutlined, WarningOutlined,
  CheckCircleOutlined, InfoCircleOutlined, RiseOutlined,
  ClockCircleOutlined, BarChartOutlined, BulbOutlined,
  ArrowUpOutlined, ArrowDownOutlined,
} from '@ant-design/icons';
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, ReferenceLine, Legend,
  BarChart, Bar, Cell,
} from 'recharts';
import type { ColumnsType } from 'antd/es/table';

import { mockPredictions, mockSensors } from '../../data/mockData';
import { PredictionResult } from '../../types';
import { useSimulatorData } from '../../context/SimulatorContext';

// ─── Constante ────────────────────────────────────────────

const RISK_COLOR: Record<string, string> = {
  critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#10b981',
};
const RISK_BG: Record<string, string> = {
  critical: '#fff5f5', high: '#fff7ed', medium: '#fffbf0', low: '#f0fdf4',
};
const RISK_LABEL: Record<string, string> = {
  critical: 'Risc critic', high: 'Risc ridicat', medium: 'Risc mediu', low: 'Risc scăzut',
};
const RISK_ICON: Record<string, React.ReactNode> = {
  critical: <FireOutlined />,
  high:     <WarningOutlined />,
  medium:   <InfoCircleOutlined />,
  low:      <CheckCircleOutlined />,
};

// ─── Tooltip grafic ───────────────────────────────────────

const ForecastTooltip: React.FC<any> = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  const isFuture = payload[0]?.payload?.isFuture;
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.10)', fontSize: 12, minWidth: 180 }}>
      <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
        {isFuture ? <span style={{ fontSize: 10, background: '#eef2ff', color: '#6366f1', padding: '1px 6px', borderRadius: 99 }}>PREDICȚIE</span> : <span style={{ fontSize: 10, background: '#f0fdf4', color: '#10b981', padding: '1px 6px', borderRadius: 99 }}>REAL</span>}
        {label}
      </div>
      {payload.map((p: any, i: number) => (
        p.name !== 'upper' && p.name !== 'lower' && (
          <div key={i} style={{ color: p.color ?? '#64748b', marginBottom: 2, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span>{p.name === 'actual' ? 'Valoare reală' : p.name === 'predicted' ? 'Predicție AI' : p.name}</span>
            <strong>{p.value} {unit}</strong>
          </div>
        )
      ))}
    </div>
  );
};

// ─── Grafic forecast ──────────────────────────────────────

const ForecastChart: React.FC<{ pred: PredictionResult }> = ({ pred }) => {
  const data = pred.points.map(p => ({
    time:      p.date,
    actual:    p.actual ?? null,
    predicted: p.predicted,
    upper:     parseFloat((p.predicted + p.confidence).toFixed(4)),
    lower:     parseFloat(Math.max(0, p.predicted - p.confidence).toFixed(4)),
    isFuture:  p.actual === undefined,
  }));

  const splitIdx  = data.findIndex(d => d.isFuture);
  const splitTime = splitIdx >= 0 ? data[splitIdx].time : null;
  const step      = Math.max(1, Math.floor(data.length / 10));
  const ticks     = data.filter((_, i) => i % step === 0).map(d => d.time);

  const futureVals = data.filter(d => d.isFuture).map(d => d.predicted);
  const maxFuture  = futureVals.length ? Math.max(...futureVals) : 0;
  const minFuture  = futureVals.length ? Math.min(...futureVals) : 0;
  const avgFuture  = futureVals.length ? futureVals.reduce((s, v) => s + v, 0) / futureVals.length : 0;

  const color = RISK_COLOR[pred.riskLevel];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
        {[
          { l: 'Min prezis 24h', v: minFuture.toFixed(4), c: '#10b981' },
          { l: 'Medie prezisă',  v: avgFuture.toFixed(4), c: '#6366f1' },
          { l: 'Max prezis 24h', v: maxFuture.toFixed(4), c: '#ef4444' },
          { l: 'Probabilitate',  v: `${pred.riskProbability}%`, c: color },
        ].map(s => (
          <div key={s.l} style={{ padding: '10px 8px', borderRadius: 9, background: '#f8fafc', border: '1px solid #f1f5f9', textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: s.c }}>{s.v}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{s.l}</div>
            <div style={{ fontSize: 9, color: '#cbd5e1' }}>{pred.unit}</div>
          </div>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={data} margin={{ top: 8, right: 24, bottom: 0, left: -8 }}>
          <defs>
            <linearGradient id="confGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={color} stopOpacity={0.15} />
              <stop offset="95%" stopColor={color} stopOpacity={0.03} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="time" tick={{ fontSize: 9, fill: '#94a3b8' }} ticks={ticks} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} domain={['auto', 'auto']} />
          <RTooltip content={<ForecastTooltip unit={pred.unit} />} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          <ReferenceLine y={mockSensors.find(s => s.id === pred.sensorId)?.lod} stroke="#f59e0b" strokeDasharray="5 3" label={{ value: 'LOD', position: 'insideTopRight', fontSize: 9, fill: '#f59e0b' }} />
          <ReferenceLine y={mockSensors.find(s => s.id === pred.sensorId)?.maxAllowed} stroke="#ef4444" strokeDasharray="4 2" label={{ value: 'MAX', position: 'insideTopRight', fontSize: 9, fill: '#ef4444' }} />
          {splitTime && <ReferenceLine x={splitTime} stroke="#6366f1" strokeDasharray="4 2" label={{ value: 'Acum', position: 'insideTopLeft', fontSize: 9, fill: '#6366f1' }} />}
          <Area type="monotone" dataKey="upper" stroke="none" fill="url(#confGrad)" name="upper" legendType="none" />
          <Area type="monotone" dataKey="lower" stroke="none" fill="#fff" name="lower" legendType="none" />
          <Line type="monotone" dataKey="actual"    stroke="#10b981" strokeWidth={2.5} dot={false} name="actual"    connectNulls={false} />
          <Line type="monotone" dataKey="predicted" stroke={color}    strokeWidth={2}   dot={false} name="predicted" strokeDasharray={splitIdx >= 0 ? "6 3" : undefined} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

// ─── AI Factors panel ────────────────────────────────────

const AIFactorsPanel: React.FC<{ pred: PredictionResult }> = ({ pred }) => {
  if (!pred.topFactors?.length) return null;

  const maxImpact = Math.max(...pred.topFactors.map(f => f.impact));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Factori */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', marginBottom: 10 }}>
          FACTORI DETERMINANȚI AI
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {pred.topFactors.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: f.direction === 'up' ? '#fee2e2' : '#d1fae5', fontSize: 10 }}>
                {f.direction === 'up' ? <ArrowUpOutlined style={{ color: '#ef4444' }} /> : <ArrowDownOutlined style={{ color: '#10b981' }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{f.name}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: f.direction === 'up' ? '#ef4444' : '#10b981', flexShrink: 0, marginLeft: 4 }}>{f.impact}%</span>
                </div>
                <div style={{ height: 4, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 99, width: `${(f.impact / maxImpact) * 100}%`, background: f.direction === 'up' ? '#ef4444' : '#10b981', transition: 'width 0.5s ease' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recomandare */}
      {pred.recommendation && (
        <div style={{ padding: '12px 14px', borderRadius: 10, background: '#fafbff', border: '1px solid #e0e7ff' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <BulbOutlined style={{ color: '#6366f1', fontSize: 14, marginTop: 1, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', marginBottom: 4 }}>Recomandare AI</div>
              <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.55 }}>{pred.recommendation}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Risk card ────────────────────────────────────────────

const RiskCard: React.FC<{ pred: PredictionResult; selected: boolean; onClick: () => void }> = ({ pred, selected: sel, onClick }) => {
  const color   = RISK_COLOR[pred.riskLevel];
  const sensor  = mockSensors.find(s => s.id === pred.sensorId);
  return (
    <div
      onClick={onClick}
      style={{
        padding: '10px 12px', borderRadius: 10, marginBottom: 6, cursor: 'pointer',
        background: sel ? RISK_BG[pred.riskLevel] : '#fafafa',
        border: `1.5px solid ${sel ? color : '#f1f5f9'}`,
        transition: 'all 0.15s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {pred.parameter}
          </div>
          <div style={{ fontSize: 10, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {sensor?.name}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 900, color, lineHeight: 1 }}>{pred.riskProbability}%</div>
          <div style={{ fontSize: 9, color }}>risc</div>
        </div>
      </div>
      <div style={{ marginTop: 6, height: 4, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 99, width: `${pred.riskProbability}%`, background: color }} />
      </div>
    </div>
  );
};

// ─── Panel principal ──────────────────────────────────────

const PredictionPanel: React.FC = () => {
  const { sensors } = useSimulatorData();
  const [selectedId, setSelectedId] = useState(mockPredictions[0]?.sensorId ?? '');

  const selected = useMemo(() =>
    mockPredictions.find(p => p.sensorId === selectedId) ?? mockPredictions[0],
    [selectedId]
  );

  const sensor        = sensors.find(s => s.id === selected?.sensorId);
  const sorted        = [...mockPredictions].sort((a, b) => b.riskProbability - a.riskProbability);
  const criticalCount = mockPredictions.filter(p => p.riskLevel === 'critical').length;
  const highCount     = mockPredictions.filter(p => p.riskLevel === 'high').length;
  const avgRisk       = Math.round(mockPredictions.reduce((s, p) => s + p.riskProbability, 0) / mockPredictions.length);

  const kpis = [
    { t: 'Predicții active', v: mockPredictions.length, c: '#6366f1', bg: '#eef2ff', i: <ExperimentOutlined /> },
    { t: 'Risc critic',       v: criticalCount,          c: '#ef4444', bg: '#fee2e2', i: <FireOutlined /> },
    { t: 'Risc ridicat',      v: highCount,              c: '#f97316', bg: '#fff7ed', i: <WarningOutlined /> },
    { t: 'Risc mediu (%)',    v: `${avgRisk}%`,          c: '#f59e0b', bg: '#fef3c7', i: <BarChartOutlined /> },
  ];

  const columns: ColumnsType<PredictionResult> = [
    {
      title: 'Parametru / Senzor', key: 'param',
      render: (_: any, r: PredictionResult) => {
        const s = mockSensors.find(x => x.id === r.sensorId);
        return (
          <div style={{ cursor: 'pointer' }} onClick={() => setSelectedId(r.sensorId)}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.parameter}</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{s?.name} · {s?.location.label}</div>
          </div>
        );
      },
    },
    {
      title: 'Probabilitate', dataIndex: 'riskProbability', key: 'prob', width: 180,
      sorter: (a, b) => b.riskProbability - a.riskProbability,
      defaultSortOrder: 'ascend',
      render: (v: number, r: PredictionResult) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, height: 6, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 99, background: RISK_COLOR[r.riskLevel], width: `${v}%` }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 800, color: RISK_COLOR[r.riskLevel], minWidth: 36 }}>{v}%</span>
        </div>
      ),
    },
    {
      title: 'Nivel risc', dataIndex: 'riskLevel', key: 'level', width: 130,
      render: (v: string) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99, background: RISK_BG[v], color: RISK_COLOR[v] }}>
          {RISK_ICON[v]} {RISK_LABEL[v]}
        </span>
      ),
    },
    {
      title: 'Factori cheie', key: 'factors', width: 220,
      render: (_: any, r: PredictionResult) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {(r.topFactors ?? []).slice(0, 2).map((f, i) => (
            <span key={i} style={{ fontSize: 9, padding: '1px 6px', borderRadius: 99, background: f.direction === 'up' ? '#fee2e2' : '#d1fae5', color: f.direction === 'up' ? '#ef4444' : '#10b981', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 2 }}>
              {f.direction === 'up' ? '↑' : '↓'} {f.name.slice(0, 20)}{f.name.length > 20 ? '…' : ''} ({f.impact}%)
            </span>
          ))}
        </div>
      ),
    },
    {
      title: 'Predicție (48h)', dataIndex: 'forecastHours', key: 'forecast', width: 110,
      render: (v: number) => <span style={{ fontSize: 12, color: '#64748b' }}>{v}h</span>,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>Predicții AI</h2>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>
          Motor predicție bazat pe trend analysis · {mockPredictions.length} substanțe analizate · Actualizat live
        </span>
      </div>

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {kpis.map((k, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', letterSpacing: '0.04em' }}>{k.t.toUpperCase()}</div>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: k.bg, fontSize: 15, color: k.c, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{k.i}</div>
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: k.c, letterSpacing: '-0.5px' }}>{k.v}</div>
          </div>
        ))}
      </div>

      {/* Grafic + Risk list + AI Factors */}
      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr 220px', gap: 16 }}>

        {/* Risk list */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflowY: 'auto', maxHeight: 520 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', marginBottom: 10 }}>
            SCOR RISC SUBSTANȚE
          </div>
          {sorted.map(p => (
            <RiskCard key={p.sensorId} pred={p} selected={selectedId === p.sensorId} onClick={() => setSelectedId(p.sensorId)} />
          ))}
        </div>

        {/* Grafic forecast */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: RISK_BG[selected.riskLevel], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: RISK_COLOR[selected.riskLevel] }}>
              {RISK_ICON[selected.riskLevel]}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
                {selected.parameter}
                <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: RISK_BG[selected.riskLevel], color: RISK_COLOR[selected.riskLevel] }}>
                  {RISK_LABEL[selected.riskLevel]} · {selected.riskProbability}%
                </span>
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{sensor?.name} · {sensor?.location.label}</div>
            </div>
          </div>
          <div style={{ padding: '16px 20px' }}>
            {selected && <ForecastChart pred={selected} />}
          </div>
        </div>

        {/* AI Factors */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
            <ExperimentOutlined style={{ color: '#6366f1' }} /> EXPLICAȚIE AI
          </div>
          {selected && <AIFactorsPanel pred={selected} />}
        </div>
      </div>

      {/* Tabel complet */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
          Tabel complet — toate substanțele monitorizate
        </div>
        <Table
          columns={columns} dataSource={sorted}
          rowKey="sensorId" pagination={false} size="middle"
          onRow={r => ({ onClick: () => setSelectedId(r.sensorId), style: { cursor: 'pointer' } })}
          rowClassName={r => r.riskLevel === 'critical' ? 'row-critical' : r.riskLevel === 'high' ? 'row-warning' : ''}
        />
      </div>
    </div>
  );
};

export default PredictionPanel;